import React, { useState, useContext } from 'react';
import { Form, Card, Input, Button, message, Select, Tooltip, Modal } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { useLocation, useHistory } from 'react-router-dom';
import { QuestionCircleOutlined } from '@ant-design/icons';

import AppContext from '../../../components/AppContext';
import ProgressModal from '../../../components/ProgressModal';

import './index.css';
import { AuthPayload, MutationLoginArgs, MutationSignupArgs, Operator } from '../../../utils/types';

const { Option } = Select;

function useURLQuery() {
  return new URLSearchParams(useLocation().search);
}

const SIGNUP_MUTATION = gql`
  mutation SignupMutation(
    $email: String!
    $password: String!
    $wechatId: String!
    $name: String!
    $operatorId: ID
  ) {
    signup(
      email: $email
      password: $password
      wechatId: $wechatId
      name: $name
      operatorId: $operatorId
    ) {
      token
      user {
        id
        code
        email
        wechatId
        name
        country
        city
        state
        zipCode
        recipientName
        phone
        address
        operator {
          id
          name
          wechatId
          phone
        }
      }
    }
  }
`;

const LOGIN_MUTATION = gql`
  mutation LoginMutation($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      user {
        id
        code
        email
        wechatId
        name
        country
        city
        state
        zipCode
        recipientName
        phone
        address
        operator {
          id
          name
          wechatId
          phone
        }
      }
    }
  }
`;

const OPERATORS_QUERY = gql`
  {
    operators {
      id
      name
    }
  }
`;

const tabList = [
  {
    key: 'login',
    tab: '登录',
  },
  {
    key: 'signup',
    tab: '注册',
  },
];

interface TabContentList {
  [tabKey: string]: JSX.Element;
}

type LoginFormType = {
  email: string,
  password: string,
};

type SignupFormType = {
  email: string,
  password: string,
  confirm_password: string,
  name: string,
  wechatId: string,
  operatorId: string,
};

function Login({ tab = 'login' }: { tab?: string }) {
  const [activeTabKey, setActiveTabKey] = useState(tab);

  const { updateAuthInfo } = useContext(AppContext);

  const query = useURLQuery();
  const from = query.get('from');

  const [loginForm] = Form.useForm<LoginFormType>();
  const [signupForm] = Form.useForm<SignupFormType>();

  const [login] = useMutation<{ login: AuthPayload }, MutationLoginArgs>(LOGIN_MUTATION, {
    onError: () => null,
    onCompleted: data => {
      ProgressModal.hide();

      saveUserData(data.login);
      message.success('登录成功');

      if (from) {
        history.replace(from);
      } else {
        history.replace('/');
      }
    },
  });
  const [signup] = useMutation<{ signup: AuthPayload }, MutationSignupArgs>(SIGNUP_MUTATION, {
    onError: () => null,
    onCompleted: data => {
      ProgressModal.hide();

      saveUserData(data.signup);
      message.success('注册成功，使用中有任何疑问都可以联系客服');
      history.replace('/');
    },
  });
  const history = useHistory();

  let confirmPasswordDirty = false;

  const onLoginFinish = (values: LoginFormType) => {
    ProgressModal.show();
    login({ variables: values });
  };

  const onSignupFinish = (values: SignupFormType) => {
    ProgressModal.show();

    signup({ variables: values });
  };

  const handleConfirmPasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    confirmPasswordDirty = confirmPasswordDirty || !!value;
  };

  const saveUserData = data => {
    updateAuthInfo(data);
  };

  const {
    loading: loadingOperators,
    data: operatorsData = { operators: [] },
  } = useQuery<{ operators: Operator[] }>(OPERATORS_QUERY, { fetchPolicy: 'cache-and-network' });

  const forgotPassword = () => {
    Modal.success({
      content: (
        <div>
          <p>若忘了密码，可以联系客服帮你重置，登录后再修改就可以啦 {'😎'}</p>
        </div>
      ),
      icon: null,
      okText: 'OK',
      onOk() {},
    });
  };

  const emailField = (
    <Form.Item
      label="邮箱"
      name="email"
      validateTrigger="onBlur"
      rules={[
        { required: true, message: '必填' },
        { type: 'email', message: '邮箱格式有误' },
      ]}
    >
      <Input allowClear autoComplete="username" />
    </Form.Item>
  );

  const contentList: TabContentList = {
    login: (
      <Form form={loginForm} layout="vertical" onFinish={onLoginFinish} scrollToFirstError>
        {emailField}

        <Form.Item label="密码" name="password" rules={[{ required: true, message: '必填' }]}>
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Form.Item>
          <Button block type="primary" htmlType="submit">
            登录
          </Button>
        </Form.Item>
        <Form.Item>
          <Button block type="link" onClick={forgotPassword}>
            忘记密码？
          </Button>
        </Form.Item>
      </Form>
    ),
    signup: (
      <Form form={signupForm} layout="vertical" onFinish={onSignupFinish} scrollToFirstError>
        {emailField}

        <Form.Item
          label="密码"
          name="password"
          dependencies={confirmPasswordDirty ? ['confirm_password'] : []}
          // validateTrigger="onBlur"
          rules={[
            { required: true, message: '必填' },
            {
              type: 'string',
              min: 6,
              message: '密码长度至少为6位',
            },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="确认密码"
          dependencies={['password']}
          name="confirm_password"
          // validateTrigger="onBlur"
          rules={[
            { required: true, message: '请再次输入密码确认' },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject('两次输入的密码不一致');
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" onBlur={handleConfirmPasswordBlur} />
        </Form.Item>

        <Form.Item
          label="名字（真实姓名或昵称或微信名）"
          name="name"
          validateTrigger="onBlur"
          rules={[{ required: true, message: '必填' }]}
        >
          <Input allowClear autoComplete="username" />
        </Form.Item>

        <Form.Item
          label={
            <span>
              微信号（注意不是微信名）&nbsp;
              <Tooltip title="为了方便操作员通过微信联系你，请提供微信号。注意是微信号不是微信名字，在微信里可以通过：点界面右下角的“我” -> 点自己头像来查看微信号">
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          }
          name="wechatId"
          rules={[{ required: true, message: '必填' }]}
        >
          <Input allowClear autoComplete="name" />
        </Form.Item>

        <Form.Item
          name="operatorId"
          label={
            <span>
              关联操作员&nbsp;
              <Tooltip title="如果你以前使用过我们的服务，请选择之前联络你的操作员；若你第一次使用，可以留空，我们会自动为你分配操作员">
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          }
        >
          <Select loading={loadingOperators} placeholder="" allowClear>
            {operatorsData.operators.map((operator) => (
              <Option key={operator.id} value={operator.id}>
                {operator.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <Button block type="primary" htmlType="submit">
            注册
          </Button>
        </Form.Item>
      </Form>
    ),
  };

  return (
    <div className="Login">
      <Card
        bordered={false}
        tabList={tabList}
        activeTabKey={activeTabKey}
        onTabChange={key => {
          setActiveTabKey(key);
        }}
      >
        {contentList[activeTabKey]}
      </Card>
    </div>
  );
}

export default Login;
